import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createWorker } from 'tesseract.js';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';

interface CameraScannerProps {
  onPlateDetected: (plate: string) => void;
  onClose: () => void;
}

const CameraScanner: React.FC<CameraScannerProps> = ({ onPlateDetected, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [detectedPlate, setDetectedPlate] = useState<string>('');
  const [cameraReady, setCameraReady] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPlate, setEditedPlate] = useState('');

  // Démarrer la caméra
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setCameraReady(false);
      
      // Arrêter le stream précédent si existant
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
        };
      }
    } catch (err: any) {
      console.error('Erreur caméra:', err);
      if (err.name === 'NotAllowedError') {
        setError('Accès à la caméra refusé. Autorisez l\'accès dans les paramètres.');
      } else if (err.name === 'NotFoundError') {
        setError('Aucune caméra trouvée sur cet appareil.');
      } else {
        setError('Impossible d\'accéder à la caméra.');
      }
    }
  }, [facingMode, stream]);

  // Démarrer la caméra au montage
  useEffect(() => {
    startCamera();
    
    return () => {
      // Nettoyer le stream à la fermeture
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Redémarrer la caméra quand on change de face
  useEffect(() => {
    if (stream) {
      startCamera();
    }
  }, [facingMode]);

  // Changer de caméra (avant/arrière)
  const toggleCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Prétraitement de l'image pour améliorer l'OCR
  const preprocessImage = (canvas: HTMLCanvasElement): string => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/jpeg', 0.9);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // 1. Conversion en niveaux de gris avec pondération
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    // 2. Augmentation du contraste
    const factor = 1.5; // Facteur de contraste
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, Math.max(0, factor * (data[i] - 128) + 128));
      data[i + 1] = Math.min(255, Math.max(0, factor * (data[i + 1] - 128) + 128));
      data[i + 2] = Math.min(255, Math.max(0, factor * (data[i + 2] - 128) + 128));
    }

    // 3. Binarisation (seuillage adaptatif simple)
    const threshold = 140;
    for (let i = 0; i < data.length; i += 4) {
      const value = data[i] > threshold ? 255 : 0;
      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  };

  // Capturer l'image (zone de la plaque uniquement)
  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Calculer la zone de capture (rectangle de la plaque)
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    // Zone de capture centrée (85% de largeur, hauteur fixe pour plaque)
    const cropWidth = videoWidth * 0.85;
    const cropHeight = videoHeight * 0.15; // Environ la hauteur d'une plaque
    const cropX = (videoWidth - cropWidth) / 2;
    const cropY = (videoHeight - cropHeight) / 2;

    // Définir la taille du canvas pour la zone recadrée
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    // Dessiner uniquement la zone de la plaque
    ctx.drawImage(
      video, 
      cropX, cropY, cropWidth, cropHeight,  // Source (zone de la plaque)
      0, 0, cropWidth, cropHeight            // Destination
    );

    // Prétraiter l'image pour améliorer l'OCR
    const processedImage = preprocessImage(canvas);
    
    // Garder aussi l'image originale pour affichage
    const displayCanvas = document.createElement('canvas');
    displayCanvas.width = videoWidth;
    displayCanvas.height = videoHeight;
    const displayCtx = displayCanvas.getContext('2d');
    if (displayCtx) {
      displayCtx.drawImage(video, 0, 0);
    }
    setCapturedImage(displayCanvas.toDataURL('image/jpeg', 0.9));

    // Lancer l'OCR sur l'image prétraitée
    processOCR(processedImage);
  };

  // Nettoyer et formater une plaque
  const cleanAndFormatPlate = (text: string): string | null => {
    // Nettoyer le texte
    let cleaned = text.toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .replace(/O/g, '0')  // O -> 0 (erreur courante)
      .replace(/I/g, '1')  // I -> 1 (erreur courante)
      .replace(/S/g, '5')  // S -> 5 (parfois)
      .replace(/B/g, '8')  // B -> 8 (parfois)
      .trim();

    // Patterns de plaques françaises
    // Nouveau format: AA-123-AA (7 caractères sans tirets)
    const newFormatRegex = /([A-Z]{2})(\d{3})([A-Z]{2})/;
    // Ancien format: 1234-AB-12 (7-8 caractères)
    const oldFormatRegex = /(\d{1,4})([A-Z]{2,3})(\d{2})/;

    // Essayer nouveau format
    const newMatch = cleaned.match(newFormatRegex);
    if (newMatch) {
      return `${newMatch[1]}-${newMatch[2]}-${newMatch[3]}`;
    }

    // Essayer ancien format
    const oldMatch = cleaned.match(oldFormatRegex);
    if (oldMatch) {
      return `${oldMatch[1]}-${oldMatch[2]}-${oldMatch[3]}`;
    }

    // Si on a au moins 7 caractères, essayer de formater
    if (cleaned.length >= 7) {
      // Tenter le format AA-123-AA
      const part1 = cleaned.slice(0, 2);
      const part2 = cleaned.slice(2, 5);
      const part3 = cleaned.slice(5, 7);
      
      if (/^[A-Z]{2}$/.test(part1) && /^\d{3}$/.test(part2) && /^[A-Z]{2}$/.test(part3)) {
        return `${part1}-${part2}-${part3}`;
      }
    }

    // Retourner le texte nettoyé s'il a une longueur raisonnable
    if (cleaned.length >= 5 && cleaned.length <= 10) {
      return cleaned;
    }

    return null;
  };

  // Traitement OCR
  const processOCR = async (imageData: string) => {
    setIsProcessing(true);
    setError(null);
    setDetectedPlate('');

    try {
      const worker = await createWorker('fra+eng');
      
      // Configurer pour les plaques d'immatriculation
      await worker.setParameters({
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
        tessedit_pageseg_mode: '7', // Traiter comme une seule ligne
      });

      const { data: { text, confidence } } = await worker.recognize(imageData);
      await worker.terminate();

      console.log('OCR brut:', text, 'Confiance:', confidence);

      // Nettoyer et formater la plaque
      const plate = cleanAndFormatPlate(text);

      if (plate) {
        setDetectedPlate(plate);
        setEditedPlate(plate);
      } else {
        setError('Plaque non reconnue. Vous pouvez la saisir manuellement.');
        setIsEditing(true);
        setEditedPlate('');
      }
    } catch (err) {
      console.error('Erreur OCR:', err);
      setError('Erreur lors de la lecture. Saisissez la plaque manuellement.');
      setIsEditing(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Réinitialiser pour nouveau scan
  const resetScan = () => {
    setCapturedImage(null);
    setDetectedPlate('');
    setError(null);
    setIsEditing(false);
    setEditedPlate('');
  };

  // Confirmer la plaque (détectée ou éditée)
  const confirmPlate = () => {
    const plateToUse = isEditing ? editedPlate : detectedPlate;
    if (plateToUse && plateToUse.length >= 5) {
      // Arrêter la caméra
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      // Formater la plaque avant de l'envoyer
      const formatted = plateToUse.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      onPlateDetected(formatted);
    }
  };

  // Passer en mode édition
  const enableEditing = () => {
    setIsEditing(true);
    setEditedPlate(detectedPlate);
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'black',
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        bgcolor: 'rgba(0,0,0,0.5)',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
      }}>
        <Typography variant="h6" color="white" fontWeight={700}>
          📷 Scanner la plaque
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Vue caméra ou image capturée */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {!capturedImage ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {/* Guide de cadrage amélioré */}
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '85%',
              maxWidth: 400,
              height: 70,
              border: '4px solid #10B981',
              borderRadius: 2,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            }}>
              {/* Coins de guidage */}
              <Box sx={{ position: 'absolute', top: -4, left: -4, width: 20, height: 20, borderTop: '4px solid #FFD700', borderLeft: '4px solid #FFD700' }} />
              <Box sx={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderTop: '4px solid #FFD700', borderRight: '4px solid #FFD700' }} />
              <Box sx={{ position: 'absolute', bottom: -4, left: -4, width: 20, height: 20, borderBottom: '4px solid #FFD700', borderLeft: '4px solid #FFD700' }} />
              <Box sx={{ position: 'absolute', bottom: -4, right: -4, width: 20, height: 20, borderBottom: '4px solid #FFD700', borderRight: '4px solid #FFD700' }} />
              
              <Typography 
                sx={{ 
                  position: 'absolute', 
                  top: -35, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  textShadow: '0 2px 4px rgba(0,0,0,0.9)',
                }}
              >
                ⬆️ Alignez la plaque ici ⬆️
              </Typography>
              <Typography 
                sx={{ 
                  position: 'absolute', 
                  bottom: -35, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  color: 'white',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  textShadow: '0 2px 4px rgba(0,0,0,0.9)',
                }}
              >
                Rapprochez-vous pour une meilleure lecture
              </Typography>
            </Box>
          </>
        ) : (
          <img 
            src={capturedImage} 
            alt="Captured" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
            }} 
          />
        )}

        {/* Canvas caché pour la capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Box>

      {/* Zone de résultat / erreur */}
      {(detectedPlate || error || isProcessing || isEditing) && (
        <Paper sx={{ 
          position: 'absolute', 
          bottom: 120, 
          left: 16, 
          right: 16,
          p: 2,
          borderRadius: 2,
          bgcolor: detectedPlate && !isEditing ? '#F0FDF4' : 'white',
          border: detectedPlate && !isEditing ? '2px solid #10B981' : '1px solid #E5E7EB',
        }}>
          {isProcessing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Lecture de la plaque en cours...</Typography>
            </Box>
          )}
          
          {/* Mode édition manuelle */}
          {isEditing && !isProcessing && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Saisissez ou corrigez la plaque :
              </Typography>
              <TextField
                fullWidth
                value={editedPlate}
                onChange={(e) => setEditedPlate(e.target.value.toUpperCase())}
                placeholder="Ex: AB-123-CD"
                autoFocus
                InputProps={{
                  sx: { 
                    fontSize: '1.5rem', 
                    fontWeight: 700,
                    textAlign: 'center',
                    '& input': { textAlign: 'center', textTransform: 'uppercase' }
                  }
                }}
              />
            </Box>
          )}
          
          {/* Plaque détectée (non édition) */}
          {detectedPlate && !isProcessing && !isEditing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ color: '#10B981', fontSize: 32 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Plaque détectée :
                </Typography>
                <Typography variant="h5" fontWeight={700} color="#166534">
                  {detectedPlate}
                </Typography>
              </Box>
              <IconButton onClick={enableEditing} size="small" sx={{ bgcolor: '#F3F4F6' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          {error && !isProcessing && !isEditing && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {error}
            </Alert>
          )}
        </Paper>
      )}

      {/* Contrôles */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
      }}>
        {!capturedImage ? (
          <>
            {/* Bouton changer caméra */}
            <IconButton 
              onClick={toggleCamera}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <FlipCameraIosIcon />
            </IconButton>

            {/* Bouton capture */}
            <Button
              variant="contained"
              onClick={captureImage}
              disabled={!cameraReady || isProcessing}
              startIcon={<CameraAltIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                bgcolor: '#10B981',
                fontSize: '1rem',
                fontWeight: 700,
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              Capturer
            </Button>

            {/* Placeholder pour équilibrer */}
            <Box sx={{ width: 48 }} />
          </>
        ) : (
          <>
            {/* Bouton recommencer */}
            <Button
              variant="outlined"
              onClick={resetScan}
              disabled={isProcessing}
              startIcon={<RefreshIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Recommencer
            </Button>

            {/* Bouton confirmer */}
            {(detectedPlate || (isEditing && editedPlate.length >= 5)) && (
              <Button
                variant="contained"
                onClick={confirmPlate}
                disabled={isProcessing}
                startIcon={<CheckCircleIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  bgcolor: '#10B981',
                  fontSize: '1rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#059669' },
                }}
              >
                Confirmer
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CameraScanner;

